/**
 * What an action can be aimed at.
 *
 * The target of an exploration action was free text for six phases, because
 * `canon_entries` existed and nothing populated it. It does now — the world
 * generator writes every canon type and the Owner can author one — so a player
 * can point at a thing the world actually contains rather than typing a name
 * the server has never heard of.
 *
 * **Audience-filtered server-side, per viewer.** `GET …/canon` runs the one
 * visibility query, so what is offered here is what this character legitimately
 * knows about. It is fetched rather than derived from the transcript for
 * exactly that reason: a client that scraped names out of messages would be
 * building a second, unaudited answer to the visibility question.
 *
 * Free text remains: the picker is an *offer*, never a requirement. Most of
 * what a party does is aimed at something the world model has no row for — a
 * loose board, a smell, a noise — and demanding a canon id would make the verb
 * bar unusable for exactly those.
 */

import { useEffect, useState } from 'react';

import { api } from '../api.js';

export interface CanonTarget {
  readonly id: string;
  readonly name: string;
  readonly type: string;
  /** What it looks like / where it lies — shown as the entry's tooltip, never as part of its name. */
  readonly description?: string;
}

interface CanonResponse {
  canon: { id: string; name: string; type: string; description?: string }[];
}

export function useCanonTargets(
  campaignId: string | undefined,
  /** Anything that changes when the world may have gained an entry. */
  token?: unknown,
): readonly CanonTarget[] {
  const [targets, setTargets] = useState<readonly CanonTarget[]>([]);

  useEffect(() => {
    if (!campaignId) return;
    void api
      .get<CanonResponse>(`/api/campaigns/${campaignId}/canon`)
      // An empty list is the honest answer for a campaign whose world has not
      // been generated yet, and it is also what a failure leaves behind — in
      // both cases the composer falls back to free text, which still works.
      .then((body) =>
        setTargets(
          (body?.canon ?? []).map((entry) => ({
            id: entry.id,
            name: entry.name,
            type: entry.type,
            ...(entry.description ? { description: entry.description } : {}),
          })),
        ),
      )
      .catch(() => setTargets([]));
  }, [campaignId, token]);

  return targets;
}

/**
 * The other living characters at the table, as targets.
 *
 * Offered for `pick_pocket` alone: it is the one verb in the set whose target
 * is a *person*, and a picker that listed the party under `search` or `force`
 * would be suggesting things the engine has no answer for.
 *
 * **Whether the attempt is allowed is not decided here.** The table's PvP-theft
 * setting is the server's gate — it refuses the action by name, in words the
 * composer shows — because a client-side omission is a suggestion and this is a
 * rule. Offering the target and having the server refuse it is the honest
 * shape: the player learns the table has theft switched off, rather than
 * finding a target quietly missing from a list.
 */
export function partyTargets(
  roster: readonly {
    readonly characterId: string;
    readonly name: string;
    readonly deathStatus?: string;
  }[],
  myCharacterId: string | null | undefined,
): readonly CanonTarget[] {
  return roster
    .filter((entry) => entry.characterId !== myCharacterId && entry.deathStatus === 'alive')
    .map((entry) => ({ id: entry.characterId, name: entry.name, type: 'at this table' }));
}

/**
 * Which canon types each exploration verb can sensibly be aimed at.
 *
 * **This table is this application's reading, not the book's.** Nothing in the
 * data says what a verb may be aimed at, and the server enforces a kind for only
 * a few: `take` wants an item, `move` a place, `attack` and `talk` a creature,
 * `pick_pocket` a person. The rest are what a player would plausibly mean (you
 * listen at a door or a person, you force a lock or a door, you do not force a
 * faction), written down once so every picker agrees. The picker is only an
 * offer: free text still works, so a wrong guess here costs a missing suggestion,
 * never a refused action.
 *
 * A verb that is not listed offers everything, which is the safe direction for
 * a verb added later.
 */
const TARGET_KINDS: Readonly<Record<string, readonly string[] | 'none'>> = {
  look: ['item', 'npc', 'place'],
  listen: ['npc', 'place'],
  recall: ['faction', 'item', 'lore', 'npc', 'place', 'quest'],
  appraise: ['item'],
  search: ['item', 'npc', 'place'],
  force: ['item', 'place'],
  take: ['item'],
  read: ['item', 'place'],
  move: ['place'],
  talk: ['npc'],
  attack: ['npc'],
  // A rest is the whole party taking one; there is nothing to aim it at.
  rest: 'none',
  climb: ['item', 'place'],
  swim: ['place'],
  hide: ['item', 'place'],
  sneak: ['npc', 'place'],
  pick_pocket: ['npc'],
  pick_lock: ['item', 'place'],
  find_traps: ['item', 'place'],
  remove_traps: ['item', 'place'],
  scout: ['place'],
  attempt: ['faction', 'item', 'lore', 'npc', 'place', 'quest'],
};

/**
 * What the target picker offers for a verb: the canon entries of the kinds in
 * `TARGET_KINDS`, plus the two lists that only some verbs widen it with — the
 * other characters at the table for `pick_pocket` (a person), and what the
 * character is carrying for `read` (the commonest thing anybody reads is a scroll
 * in their own pack).
 */
export function targetsFor(
  verb: string | undefined,
  lists: {
    readonly canon: readonly CanonTarget[];
    readonly party: readonly CanonTarget[];
    readonly carried: readonly CanonTarget[];
    /**
     * The only places a `move` can go from here: the ids of the current dungeon room's exits. Absent when the
     * party is not standing in a room, where every place is a destination. A room has no coordinates, so an
     * overland journey from it cannot be costed and the server refuses anything else (Maz, 2026-10-21).
     */
    readonly reachable?: ReadonlySet<string> | undefined;
  },
): readonly CanonTarget[] {
  if (verb === undefined) return lists.canon;
  const kinds = TARGET_KINDS[verb];
  if (kinds === 'none') return [];
  const kindMatches = kinds ? lists.canon.filter((target) => kinds.includes(target.type)) : lists.canon;
  const canon =
    verb === 'move' && lists.reachable ? kindMatches.filter((target) => lists.reachable!.has(target.id)) : kindMatches;
  if (verb === 'pick_pocket') return [...lists.party, ...canon];
  if (verb === 'read') return [...lists.carried, ...canon];
  return canon;
}

export function byType(
  targets: readonly CanonTarget[],
): readonly (readonly [string, readonly CanonTarget[]])[] {
  const groups = new Map<string, CanonTarget[]>();
  for (const target of targets) {
    const list = groups.get(target.type) ?? [];
    list.push(target);
    groups.set(target.type, list);
  }
  return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
}

/**
 * What this character is carrying, as targets for `read`.
 *
 * Offered for that one verb, on the same principle as `partyTargets` for
 * `pick_pocket`: the commonest thing anybody reads is a scroll in their own
 * pack, and a picker that listed the pack under `search` or `force` would be
 * suggesting things the engine has no answer for.
 *
 * **The id is the item's name**, not a uuid, because a sheet item has no id of
 * its own — the server matches the pack by name and canon by uuid, and the two
 * cannot collide. Free text still works either way: the picker is an offer.
 */
export function useCarriedTargets(
  characterId: string | null | undefined,
  /** Anything that changes when the pack may have changed. */
  token?: unknown,
): readonly CanonTarget[] {
  const [carried, setCarried] = useState<readonly CanonTarget[]>([]);

  useEffect(() => {
    if (!characterId) {
      setCarried([]);
      return;
    }
    void api
      .get<{ character?: { inventory?: { name: string }[] } }>(`/api/characters/${characterId}`)
      // An empty pack and a failed fetch land in the same place, and both leave
      // the composer on free text, which still works.
      .then((body) =>
        setCarried(
          (body?.character?.inventory ?? []).map((item) => ({
            id: item.name,
            name: item.name,
            type: 'carried',
          })),
        ),
      )
      .catch(() => setCarried([]));
  }, [characterId, token]);

  return carried;
}

/**
 * The names of the non-combat skills this character has learned, for the check menu. Maz, 2026-10-20: the
 * menu offers only what the character has, plus the plain ability checks anybody can make. Unknown (not yet
 * loaded, or a failed fetch) is an empty list: abilities only, which still works.
 */
export function useOwnSkillNames(
  characterId: string | null | undefined,
  token?: unknown,
): ReadonlySet<string> {
  const [names, setNames] = useState<ReadonlySet<string>>(new Set());

  useEffect(() => {
    if (!characterId) {
      setNames(new Set());
      return;
    }
    void api
      .get<{ character?: { skills?: { nonCombatSkills?: { name: string }[] } } }>(`/api/characters/${characterId}`)
      .then((body) => setNames(new Set((body?.character?.skills?.nonCombatSkills ?? []).map((skill) => skill.name))))
      .catch(() => setNames(new Set()));
  }, [characterId, token]);

  return names;
}
